import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Mail, Phone, Search, Users } from 'lucide-react';
import PageHero from '../components/common/PageHero';
import api from '../services/api';
import { alumniPhotoUrl } from '../utils/assets';

const AlumniDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [alumni, setAlumni] = useState([]);
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');

  useEffect(() => {
    fetchAlumni();
  }, []);

  useEffect(() => {
    if (id && alumni.length > 0) {
      const found = alumni.find(a => a.id === parseInt(id));
      if (found) {
        setSelectedAlumni(found);
      } else if (alumni.length > 0) {
        setSelectedAlumni(alumni[0]);
        navigate(`/alumni/${alumni[0].id}`, { replace: true });
      }
    } else if (alumni.length > 0 && !selectedAlumni) {
      setSelectedAlumni(alumni[0]);
      navigate(`/alumni/${alumni[0].id}`, { replace: true });
    }
  }, [alumni, id, navigate]);

  const fetchAlumni = async () => {
    setLoading(true);
    try {
      const response = await api.get('/alumni');
      setAlumni(response.data);
    } catch (error) {
      console.error('Error fetching alumni:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAlumni = (alumnus) => {
    setSelectedAlumni(alumnus);
    navigate(`/alumni/${alumnus.id}`);
  };

  const getRoleBadgeClass = (role) => {
    switch(role) {
      case 'Chairman': return 'bg-yellow-100 text-yellow-800';
      case 'Vice Chairman': return 'bg-orange-100 text-orange-800';
      case 'Secretary': return 'bg-blue-100 text-blue-800';
      case 'Treasurer': return 'bg-green-100 text-green-800';
      case 'Patron': return 'bg-purple-100 text-purple-800';
      case 'Advisor': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-ink-100 text-ink-800';
    }
  };

  const getImageUrl = (photoUrl) => {
    const resolved = alumniPhotoUrl(photoUrl);
    if (!resolved) return null;
    return `${resolved}?t=${Date.now()}`;
  };

  const roles = ['all', ...new Set(alumni.map(a => a.role))];

  const filteredAlumni = alumni.filter(alumnus => {
    const matchesSearch = searchTerm === '' ||
      alumnus.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alumnus.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (alumnus.department_name && alumnus.department_name.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesRole = selectedRole === 'all' || alumnus.role === selectedRole;

    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-brand-700"></div>
      </div>
    );
  }

  return (
    <div className="bg-ink-50">
      <PageHero
        icon={Users}
        title="Katine TTI Alumni Association"
        subtitle="Meet our distinguished alumni who are making a difference"
      />

      <div className="container-page py-6 sm:py-8">
        {alumni.length === 0 ? (
          <div className="card p-8 text-center">
            <span className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-brand-50 text-brand-700">
              <Users className="h-8 w-8" aria-hidden="true" />
            </span>
            <h3 className="mb-2 text-lg font-semibold text-ink-900">No Alumni Found</h3>
            <p className="text-ink-500">Check back later to meet our distinguished alumni.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-6 lg:flex-row">
            {/* Left Sidebar - Alumni List */}
            <div className="lg:w-1/3 xl:w-1/4">
              <div className="card sticky top-4 overflow-hidden p-0">
                <div className="bg-brand-700 p-4 text-white">
                  <h2 className="text-lg font-semibold">All Alumni</h2>
                  <p className="text-sm text-white/80">{filteredAlumni.length} members</p>
                </div>

                <div className="border-b border-ink-200 p-3">
                  <div className="relative">
                    <Search
                      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400"
                      aria-hidden="true"
                    />
                    <input
                      type="text"
                      placeholder="Search by name, role or department..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="input-field pl-9 text-sm"
                    />
                  </div>
                </div>

                <div className="overflow-x-auto border-b border-ink-200 p-3">
                  <div className="flex flex-wrap gap-2">
                    {roles.map(role => (
                      <button
                        key={role}
                        onClick={() => setSelectedRole(role)}
                        className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                          selectedRole === role
                            ? 'bg-brand-700 text-white'
                            : 'bg-ink-200 text-ink-700 hover:bg-ink-300'
                        }`}
                      >
                        {role === 'all' ? 'All' : role}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="max-h-[500px] divide-y divide-ink-100 overflow-y-auto">
                  {filteredAlumni.length === 0 ? (
                    <div className="p-4 text-center text-ink-500">No alumni found matching your search.</div>
                  ) : (
                    filteredAlumni.map((alumnus) => (
                      <div
                        key={alumnus.id}
                        onClick={() => handleSelectAlumni(alumnus)}
                        className={`cursor-pointer p-3 transition-all duration-200 hover:bg-ink-50 ${
                          selectedAlumni?.id === alumnus.id
                            ? 'border-l-4 border-l-brand-700 bg-brand-50'
                            : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-full bg-ink-100">
                            {getImageUrl(alumnus.photo_url) ? (
                              <img
                                src={getImageUrl(alumnus.photo_url)}
                                alt={alumnus.name}
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(alumnus.name)}&background=1e3a8a&color=fff&size=100`;
                                }}
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-brand-800 to-brand-600">
                                <span className="text-sm font-bold text-white">{alumnus.name?.charAt(0)}</span>
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="truncate text-sm font-semibold text-ink-900">{alumnus.name}</h3>
                            <div className="mt-0.5 flex items-center gap-2">
                              <span className={`inline-block rounded-full px-1.5 py-0.5 text-xs font-medium ${getRoleBadgeClass(alumnus.role)}`}>
                                {alumnus.role}
                              </span>
                              {alumnus.department_name && (
                                <span className="truncate text-xs text-ink-500">{alumnus.department_name}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Right Side - Selected Alumni Profile */}
            <div className="lg:w-2/3 xl:w-3/4">
              {selectedAlumni && (
                <div className="card overflow-hidden p-0">
                  <div className="flex flex-col md:flex-row">
                    {/* Image Section - Left side */}
                    <div className="bg-gradient-to-br from-brand-50 to-gold-50 md:w-1/3">
                      <div className="sticky top-0">
                        {getImageUrl(selectedAlumni.photo_url) ? (
                          <img
                            src={getImageUrl(selectedAlumni.photo_url)}
                            alt={selectedAlumni.name}
                            className="h-auto w-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedAlumni.name)}&background=1e3a8a&color=fff&size=400`;
                            }}
                          />
                        ) : (
                          <div className="flex aspect-square w-full items-center justify-center bg-gradient-to-br from-brand-800 to-brand-600">
                            <span className="text-6xl font-bold text-white">{selectedAlumni.name?.charAt(0)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Details Section - Right side */}
                    <div className="p-6 md:w-2/3">
                      <div className="mb-4">
                        <h1 className="text-2xl font-bold text-ink-900">{selectedAlumni.name}</h1>
                        <span className={`mt-2 inline-block rounded-full px-3 py-1 text-sm font-medium ${getRoleBadgeClass(selectedAlumni.role)}`}>
                          {selectedAlumni.role}
                        </span>
                      </div>

                      <div className="space-y-3 text-sm">
                        {selectedAlumni.department_name && (
                          <div className="flex flex-col border-b border-ink-100 py-2 sm:flex-row sm:justify-between">
                            <span className="font-semibold text-ink-700 sm:w-1/3">Department:</span>
                            <span className="text-ink-900 sm:w-2/3">{selectedAlumni.department_name}</span>
                          </div>
                        )}

                        {selectedAlumni.graduation_year && (
                          <div className="flex flex-col border-b border-ink-100 py-2 sm:flex-row sm:justify-between">
                            <span className="font-semibold text-ink-700 sm:w-1/3">Graduation Year:</span>
                            <span className="text-ink-900 sm:w-2/3">{selectedAlumni.graduation_year}</span>
                          </div>
                        )}

                        {selectedAlumni.employment_status && (
                          <div className="flex flex-col border-b border-ink-100 py-2 sm:flex-row sm:justify-between">
                            <span className="font-semibold text-ink-700 sm:w-1/3">Employment Status:</span>
                            <span className="text-ink-900 sm:w-2/3">{selectedAlumni.employment_status}</span>
                          </div>
                        )}

                        {selectedAlumni.employer && (
                          <div className="flex flex-col border-b border-ink-100 py-2 sm:flex-row sm:justify-between">
                            <span className="font-semibold text-ink-700 sm:w-1/3">Employer:</span>
                            <span className="text-ink-900 sm:w-2/3">{selectedAlumni.employer}</span>
                          </div>
                        )}

                        {selectedAlumni.email && (
                          <div className="flex flex-col border-b border-ink-100 py-2 sm:flex-row sm:justify-between">
                            <span className="font-semibold text-ink-700 sm:w-1/3">Email:</span>
                            <span className="text-ink-900 sm:w-2/3">
                              <a
                                href={`mailto:${selectedAlumni.email}`}
                                className="inline-flex items-center gap-1.5 text-brand-700 hover:underline"
                              >
                                <Mail className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                                {selectedAlumni.email}
                              </a>
                            </span>
                          </div>
                        )}

                        {selectedAlumni.phone && (
                          <div className="flex flex-col border-b border-ink-100 py-2 sm:flex-row sm:justify-between">
                            <span className="font-semibold text-ink-700 sm:w-1/3">Phone:</span>
                            <span className="text-ink-900 sm:w-2/3">
                              <a
                                href={`tel:${selectedAlumni.phone}`}
                                className="inline-flex items-center gap-1.5 text-brand-700 hover:underline"
                              >
                                <Phone className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                                {selectedAlumni.phone}
                              </a>
                            </span>
                          </div>
                        )}
                      </div>

                      {selectedAlumni.journey_description && (
                        <div className="mt-6 border-t border-ink-100 pt-4">
                          <h3 className="mb-2 font-semibold text-ink-900">My Journey</h3>
                          <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-700">
                            {selectedAlumni.journey_description}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlumniDetails;
